import { type FieldValues, useController } from 'react-hook-form';

import {
  DropdownTreeSingleSelect,
  type DropdownTreeSingleSelectProps,
} from '~/components/DropdownTreeSingleSelect/DropdownTreeSingleSelect';

import { FormField, type FormFieldProps } from '../../FormField';

type FormFieldDropdownTreeSingleSelectProps<
  TOptionProps,
  TFormFields extends FieldValues,
> = Omit<FormFieldProps<TFormFields>, 'children'> &
  Omit<
    DropdownTreeSingleSelectProps<TOptionProps>,
    'name' | 'className' | 'value' | 'onValueChange'
  >;

export function FormFieldDropdownTreeSingleSelect<
  TOptionProps = unknown,
  TFormFields extends FieldValues = FieldValues,
>({
  name,
  label,
  className,
  disabled = false,
  readOnly = false,
  triggerClassName,
  labelAddon,
  ...dropdownProps
}: FormFieldDropdownTreeSingleSelectProps<TOptionProps, TFormFields>) {
  const {
    field: { ref: _ref, ...fieldWithoutRef },
    fieldState: { invalid },
  } = useController({ name, disabled });

  return (
    <FormField
      name={name}
      label={label}
      className={className}
      disabled={disabled}
      readOnly={readOnly}
      labelAddon={labelAddon}
    >
      <DropdownTreeSingleSelect
        {...fieldWithoutRef}
        onValueChange={(value) => {
          fieldWithoutRef.onChange(value);
        }}
        disabled={disabled || readOnly}
        radix={{
          ...dropdownProps.radix,
          selectTrigger: {
            ...dropdownProps.radix?.selectTrigger,
            'aria-labelledby': name,
            'aria-invalid': invalid,
            className: triggerClassName,
          },
        }}
        {...dropdownProps}
      />
    </FormField>
  );
}

FormFieldDropdownTreeSingleSelect.displayName =
  'FormFieldDropdownTreeSingleSelect';
